import { useTranslation } from 'react-i18next';
import { getAllApartments } from '../../data/apartments';
import { trackEvent } from '../../services/analyticsService';
import { applyImageFallback } from '../../utils/imageFallback';

const AMENITY_KEY_MAP: Record<string, string> = {
  'Entire Apartment': 'entireApartment',
  'Private Kitchen': 'privateKitchen',
  'Private Bathroom': 'privateBathroom',
  'Sea View': 'seaView',
  'Mountain View': 'mountainView',
  'Landmark View': 'landmarkView',
  'Air Conditioning': 'airConditioning',
  'Flat-screen TV': 'flatScreenTv',
  Terrace: 'terrace',
  'Free WiFi': 'freeWifi',
  'Free Toiletries': 'freeToiletries',
  Kitchen: 'kitchen',
  Toilet: 'toilet',
  'Bath or Shower': 'bathOrShower',
  Towels: 'towels',
  Linen: 'linen',
  'Socket Near Bed': 'socketNearBed',
  'Tile or Marble Floor': 'tileOrMarbleFloor',
  'Sitting Area': 'sittingArea',
  TV: 'tv',
  Slippers: 'slippers',
  Refrigerator: 'refrigerator',
  Iron: 'iron',
  'Hair Dryer': 'hairDryer',
  Kitchenware: 'kitchenware',
  Kitchenette: 'kitchenette',
  'Extra Long Beds (> 2m)': 'extraLongBeds',
  'Electric Kettle': 'electricKettle',
  'Outdoor Furniture': 'outdoorFurniture',
  Wardrobe: 'wardrobe',
  Stovetop: 'stovetop',
  Toaster: 'toaster',
  'Dining Table': 'diningTable',
  'Upper Floors Accessible by Stairs Only': 'upperFloorsStairsOnly',
  Detached: 'detached',
  'Clothes Rack': 'clothesRack',
  'Toilet Paper': 'toiletPaper',
  'Hand Sanitizer': 'handSanitizer',
};

export default function ApartmentsSection() {
  const { t } = useTranslation();
  const apartments = getAllApartments();

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const openSuiteGallery = (id: number) => {
    trackEvent('select_content', {
      content_type: 'suite',
      content_id: `suite_${id}`,
      section_id: 'apartments',
    });
    globalThis.dispatchEvent(new CustomEvent('villaspilia:selectSuite', { detail: { id } }));
    scrollToSection('gallery');
  };

  return (
    <section id="apartments" className="suites section">
      <div className="container">
        <header className="section-head section-head--center">
          <p className="eyebrow eyebrow--center reveal">{t('apartments.eyebrow')}</p>
          <h2 className="reveal">{t('apartments.title')}</h2>
          <p className="lede reveal">{t('apartments.subtitle')}</p>
        </header>

        <div className="suites__grid">
          {apartments.map((apt, i) => {
            const coverUrl = apt.mainImageUrl ?? apt.photos[0]?.imageUrl;
            const coverCaption = apt.photos[0]?.caption ?? t(`apartments.suites.${apt.id}.name`, { defaultValue: apt.name });
            const suiteName = t(`apartments.suites.${apt.id}.name`, { defaultValue: apt.name });
            const suiteDescription = t(`apartments.suites.${apt.id}.description`, { defaultValue: apt.description });
            return (
              <article key={apt.id} className="suite-card reveal" style={{ ['--delay' as never]: `${i * 0.08}s` }}>
                <div className="suite-card__media">
                  {coverUrl && (
                    <img
                      src={coverUrl}
                      alt={coverCaption}
                      loading="lazy"
                      onError={(e) => applyImageFallback(e.currentTarget, suiteName, coverCaption)}
                    />
                  )}
                  <span className="suite-card__index">0{i + 1}</span>
                </div>
                <div className="suite-card__body">
                  <h3 className="suite-card__name">{suiteName}</h3>
                  <p className="suite-card__desc">{suiteDescription}</p>

                  <ul className="suite-card__amenities" aria-label={t('apartments.amenities')}>
                    {apt.amenities.slice(0, 8).map((a) => (
                      <li key={a} className="amenity">
                        {t(`apartments.amenityLabels.${AMENITY_KEY_MAP[a] ?? a}`, { defaultValue: a })}
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    className="suite-card__action"
                    onClick={() => openSuiteGallery(apt.id)}
                  >
                    {t('apartments.viewGallery')}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
